import * as React from 'react';
import { useCallback, useState, useMemo } from 'react';
import { css } from '@emotion/core';
import { Str } from '../localization/translationKeys';
import { useMsgText } from '../localization/hooks';
import ModalHeader from '../layout/modal/ModalHeader';
import LocalDiv from '../localization/LocalDiv';
import Modal from '../layout/modal/Modal';
import { FLEX_COLUMN, FLEX_ROW } from '../layout/flexBox';
import LocalBtn from '../localization/LocalBtn';
import Spacer from '../layout/Spacer';
import { COLORS } from '../globalStyle/colors';
import { btnStyle } from '../forms/styles/button';
import { SELECT } from '../forms/styles/select';
import { INPUT } from '../forms/styles/input';
import Msg from '../localization/Msg';
import { ReportRecipientReport } from './models';
import { useFormik } from 'formik';
import { deliverReportFormValidation } from '../project/survey/raters/formValidations';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ClamShell from '../layout/ClamShell';

const styles = {
  modal:       [
    FLEX_COLUMN,
    css`
      min-height: 0;
    `,
  ],
  root:        [
    FLEX_COLUMN,
    css`
      transition: opacity .5s ease;
      justify-content: space-between;
    `,
  ],
  formInputs:  [
    FLEX_COLUMN,
    css`
      align-items: stretch;
      margin: 0 20px;
    `,
  ],
  loadingForm: css`
    opacity: .4;
  `,
  radioBtn:    css`
    width: 18px;
    height: 18px;
    flex-basis: 18px;
    flex-shrink: 0;
    flex-grow: 0;
  `,
  optionGroup: [
    FLEX_ROW,
    css`
      transition: opacity .5s ease;
      color: ${COLORS.DARK_GREY};
      font-size: 12px;
    `,
  ],
  optionRadio: [
    FLEX_COLUMN,
    css`
      align-items: left;
      vertical-align: top;
      margin-right: 20px;
    `,
  ],
  optionColumn: [
    FLEX_COLUMN,
  ],
  optionRow: [
    FLEX_ROW,
    css`
     margin-top: 20px;
    `,
  ],  
  select:      [
    SELECT,
    css`
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 320px;
      min-width: 0;
      max-width: 320px;
    `,
  ],
  optionTxt:       css`
    margin: 0 0 0 10px;
    font-weight: bold;
  `,
  optionMsg:       css`
    margin: 5px 0 0 10px;
    font-style: italic;
  `,
  orMsg:       css`
    margin: 0 50px; /* weird margin is to indent text... */
  `,
  userMsg:       {
    root:    css`
      align-items: flex-end;
      font-size: 12px;
      margin: 10px 0 0 10px; /* weird right margin is to indent text... */
    `,
    icon:    css`
      font-size: 22px;
    `,
    msgText: css``,
  },
  firstLastTxtBox:      [
    INPUT(),
    css`
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 100%;
      min-width: 0;
      max-width: 50%;
      border: none;
    `,
  ],
  emailTxtBox:      [
    INPUT(),
    css`
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 100%;
      min-width: 0;
      max-width: 100%;
      margin-top: 20px;
    `,
  ],
  displayFlex:    css`
    display: flex;
  `,
  disabled:    css`
    opacity: .4;
  `,
  btnRow:      [
    FLEX_ROW,
    css`
      justify-content: flex-end;
      margin: 50px 20px 20px;
    `,
  ],
  saveBtn:     [
    btnStyle.primary(COLORS.BLUE),
    css`
      min-width: 145px;
    `,
  ],
  cancelBtn:   [
    btnStyle.secondary(COLORS.BLUE),
    css`
      min-width: 145px;
    `,
  ],
};


type Props = {
  reports: ReportRecipientReport[];
  deliverReport: (
    reports: ReportRecipientReport[], 
    toParticipant: boolean, 
    firstName: string, 
    lastName: string, 
    email: string,
  ) => void;
  dismiss: () => void;
  className?: string;
};


/**
 * DeliverReportsModal: a modal component that renders a form for delivering reports.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const DeliverReportsModal: React.FC<Props> = props => {
  const title            = useMsgText(Str.WhoShouldReceive);
  const firstPlaceholder = useMsgText(Str.FirstName);
  const lastPlaceholder  = useMsgText(Str.LastName);
  const emailPlaceholder = useMsgText(Str.Email);
  const participants     = useMsgText(Str.Participants);
  const participantMsg   = useMsgText(Str.ParticipantMsg);
  const thirdParty       = useMsgText(Str.ThirdParty);
  const thirdPartyMsg    = useMsgText(Str.ThirdPartyMsg);
  const invalidEmail     = useMsgText(Str.InvalidEmail);
  const includeName      = useMsgText(Str.NameRequired);
  
  const isComposite = useMemo(() => !props.reports.every(e => !e.isComposite), [props.reports]);

  const [deliverParticipant, setDeliverParticipant] = useState<boolean>(!isComposite);

  const onCancel = useCallback(
    event => {
      event.preventDefault();
      props.dismiss();
    },
    [props],
  );

  const pickParticipant = useCallback(() => setDeliverParticipant(true), [setDeliverParticipant]);
  const pickThirdParty  = useCallback(() => setDeliverParticipant(false), [setDeliverParticipant]);

  const { handleSubmit, handleChange, resetForm, isValid, values, setErrors } = useFormik({
    initialValues:      {
      firstName: '',
      lastName:  '',
      email:     '',
    },
    onSubmit:           (values) => {
      props.deliverReport(
        props.reports,
        deliverParticipant,
        values.firstName || '',
        values.lastName || '',
        values.email || '',
      );

      resetForm();
      setErrors({});
      props.dismiss();
    },
    validationSchema:   deliverReportFormValidation(invalidEmail, includeName, !deliverParticipant),
    validateOnChange:   false,
    enableReinitialize: true,
  });

  const allowSubmit = useMemo(
    () => isComposite || deliverParticipant 
      ? true
      : !!values.firstName?.trim().length && !!values.lastName?.trim().length && !!values.email?.trim().length, 
    [isComposite, deliverParticipant, values],
  );

  React.useEffect(() => setDeliverParticipant(!isComposite), [isComposite])

  return (
    <Modal onDismiss={props.dismiss} dismissable={true} css={styles.modal} className={props.className}>
      <ModalHeader title={title} onDismiss={props.dismiss} />
      <form
        onReset={onCancel}
        onSubmit={handleSubmit}
        css={styles.root}
      >
        <LocalDiv css={styles.formInputs}>
          {
            !isComposite && (
              <LocalDiv
                css={[styles.optionGroup, !deliverParticipant && styles.disabled]}
                onClick={pickParticipant}
              >
                <LocalDiv css={[styles.optionColumn, styles.optionRadio]}>
                  <input
                    type='radio'
                    css={styles.radioBtn}
                    checked={deliverParticipant}
                    onChange={pickParticipant}
                    name='deliverTo'
                  />
                </LocalDiv>
                <LocalDiv css={styles.optionColumn}>
                  <LocalDiv css={styles.optionTxt}>
                    {participants}
                  </LocalDiv>
                  <LocalDiv css={styles.optionMsg}>
                    {participantMsg}
                  </LocalDiv>
                </LocalDiv>
              </LocalDiv>
            )
          }
          {
            !isComposite && (
              <>
                <Spacer sizePx={15} />
                <Msg str={Str.Or} css={styles.orMsg} />
                <Spacer sizePx={15} />
              </>
            )
          }
          {
            <LocalDiv
              css={[styles.optionGroup, deliverParticipant && styles.disabled]}
              onClick={pickThirdParty}
            >
              <LocalDiv css={[styles.optionColumn, styles.optionRadio]}>
                <input
                  type='radio'
                  css={styles.radioBtn}
                  checked={!deliverParticipant}
                  onChange={pickThirdParty}
                  name='deliverTo'
                />
              </LocalDiv>
              <LocalDiv css={styles.optionColumn}>
                <LocalDiv css={styles.optionTxt}>
                  {thirdParty}
                </LocalDiv>
                <LocalDiv css={styles.optionMsg}>
                  {thirdPartyMsg}
                </LocalDiv>
                <LocalDiv css={styles.optionRow}>
                  <input
                      type='text'
                      value={values.firstName}
                      onChange={handleChange}
                      placeholder={firstPlaceholder}
                      name='firstName'
                      css={styles.firstLastTxtBox}
                  />
                  <Spacer />
                  <input
                      type='text'
                      value={values.lastName}
                      onChange={handleChange}
                      placeholder={lastPlaceholder}
                      name='lastName'
                      css={styles.firstLastTxtBox}                      
                  />
                </LocalDiv>
                <LocalDiv css={styles.displayFlex}>
                  <input
                      type='text'
                      value={values.email}
                      onChange={handleChange}
                      placeholder={emailPlaceholder}
                      name='email'
                      css={[styles.emailTxtBox, css`border-color: ${!isValid ? COLORS.RED : COLORS.WHITE};`]}
                  />
                </LocalDiv>
                <ClamShell
                    css={[FLEX_ROW, styles.userMsg.root, css`color: ${COLORS.RED}`]}
                    show={!deliverParticipant && !isValid}
                >
                  <FontAwesomeIcon icon={faExclamationTriangle} css={styles.userMsg.icon} size={'lg'} />
                  <Spacer sizePx={10} />
                  <Msg str={Str.InvalidEmail} />
                </ClamShell>
              </LocalDiv>
            </LocalDiv>
        }
        </LocalDiv>

        {/* Submit/Cancel */}
        <LocalDiv css={styles.btnRow}>
          <LocalBtn type='reset' str={Str.Cancel} css={styles.cancelBtn} />
          <Spacer sizePx={5} />
          <LocalBtn
            type='submit'
            str={Str.DeliverReports}
            disabled={!allowSubmit}
            css={styles.saveBtn}
          />
        </LocalDiv>
      </form>
    </Modal>
  );
};

export default DeliverReportsModal;
